import React, { useState, useEffect } from 'react';
import { X, Send, Check } from 'lucide-react';

const EnquiryPopup = () => {
  const [isOpen, setIsOpen] = useState(true);
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
    agreeTerms: false,
    receiveUpdates: false
  });
  const [isSubmitted, setIsSubmitted] = useState(false);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log('Form submitted:', formData);
    setIsSubmitted(true);
    
    setTimeout(() => {
      setIsSubmitted(false);
      setIsOpen(false);
      document.body.style.overflow = 'auto';
      setFormData({
        fullName: '',
        email: '',
        phone: '',
        subject: '',
        message: '',
        agreeTerms: false,
        receiveUpdates: false
      });
    }, 2000);
  };

  const handleClose = () => {
    setIsOpen(false);
    document.body.style.overflow = 'auto';
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-sm animate-slideUp">
          <div className="bg-gradient-to-r from-[#1e3a5f] to-[#1e40af] text-white px-4 py-3 rounded-t-xl flex justify-between items-center">
            <div>
              <h2 className="text-base font-bold">ENQUIRY FORM</h2>
              <p className="text-xs opacity-90">We'd love to hear from you</p>
            </div>
            <button 
              onClick={handleClose}
              className="hover:bg-white/20 p-1 rounded-full transition-colors"
            >
              <X size={18} />
            </button>
          </div>
          {isSubmitted && (
            <div className="m-3 bg-green-50 border border-green-200 rounded-lg p-2 flex items-center gap-2">
              <div className="bg-green-500 rounded-full p-0.5">
                <Check size={12} className="text-white" />
              </div>
              <p className="text-xs font-semibold text-green-800">Enquiry Sent Successfully!</p>
            </div>
          )}
          <form onSubmit={handleSubmit} className="p-4 space-y-3">
            <div>
              <label className="block text-gray-700 font-semibold text-xs mb-1">
                Full Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="fullName"
                value={formData.fullName}
                onChange={handleChange}
                required
                className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-[#1e3a5f] focus:ring-2 focus:ring-[#1e3a5f]/20 transition-colors"
                placeholder="Enter your full name"
              />
            </div>
            <div>
              <label className="block text-gray-700 font-semibold text-xs mb-1">
                Email Address <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-[#1e3a5f] focus:ring-2 focus:ring-[#1e3a5f]/20 transition-colors"
                placeholder="your@email.com"
              />
            </div>
            <div>
              <label className="block text-gray-700 font-semibold text-xs mb-1">
                Phone Number <span className="text-red-500">*</span>
              </label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                required
                className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-[#1e3a5f] focus:ring-2 focus:ring-[#1e3a5f]/20 transition-colors"
                placeholder=" 089537 86652"
              />
            </div>
            <div>
              <label className="block text-gray-700 font-semibold text-xs mb-1">
                Select Subject <span className="text-red-500">*</span>
              </label>
              <select
                name="subject"
                value={formData.subject}
                onChange={handleChange}
                required
                className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-[#1e3a5f] focus:ring-2 focus:ring-[#1e3a5f]/20 transition-colors bg-white"
              >
                <option value="">Select a subject</option>
                <option value="product">Product Inquiry</option>
                <option value="order">Order Status</option>
                <option value="returns">Returns & Exchange</option>
                <option value="feedback">Feedback</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div>
              <label className="block text-gray-700 font-semibold text-xs mb-1">
                Your Message
              </label>
              <textarea
                name="message"
                value={formData.message}
                onChange={handleChange}
                rows="2"
                className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-[#1e3a5f] focus:ring-2 focus:ring-[#1e3a5f]/20 transition-colors resize-none"
                placeholder="Tell us about your requirements..."
              ></textarea>
            </div>
            <div className="space-y-2">
              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  name="agreeTerms"
                  checked={formData.agreeTerms}
                  onChange={handleChange}
                  required
                  className="mt-0.5 w-3 h-3 text-[#1e3a5f] focus:ring-[#1e3a5f] border-gray-300 rounded"
                />
                <span className="text-xs text-gray-700 leading-tight">
                  I agree to the <a href="#" className="text-[#1e3a5f] hover:underline font-medium">Terms & Conditions</a> and <a href="#" className="text-[#1e3a5f] hover:underline font-medium">Privacy Policy</a>.
                </span>
              </label>
              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  name="receiveUpdates"
                  checked={formData.receiveUpdates}
                  onChange={handleChange}
                  className="mt-0.5 w-3 h-3 text-[#1e3a5f] focus:ring-[#1e3a5f] border-gray-300 rounded"
                />
                <span className="text-xs text-gray-600 leading-tight">
                  I would like to receive updates from DP Jewellers via Email, SMS, WhatsApp, and RCS.
                </span>
              </label>
            </div>
            <button
              type="submit"
              className="w-full bg-gradient-to-r from-[#1e3a5f] to-[#1e40af] hover:from-[#153057] hover:to-[#1a3699] text-white font-semibold py-2 px-4 rounded-lg transition-all duration-300 flex items-center justify-center gap-2 text-sm"
            >
              <Send size={14} />
              SEND ENQUIRY
            </button>
          </form>
        </div>
      </div>
      <style>{`
        @keyframes slideUp {
          from {
            transform: translateY(20px);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
        
        .animate-slideUp {
          animation: slideUp 0.3s ease-out;
        }
      `}</style>
    </>
  );
};

export default EnquiryPopup;